import type { QueryClient } from '@tanstack/react-query';

type DebounceEntry = {
  timeoutId: ReturnType<typeof setTimeout>;
  nextRunAt: number;
};

const unreadDebounceByClient = new WeakMap<QueryClient, DebounceEntry>();

/**
 * Debounced invalidation for inbox unread counts.
 * Prevents multiple subsystems from triggering duplicate invalidate and refetch bursts.
 * Ensures at most one pending invalidation per QueryClient.
 * The earliest requested execution wins.
 */
export function invalidateUnreadInquiriesDebounced(
  queryClient: QueryClient,
  delayMs: number = 750
) {
  const now = Date.now();

  const candidateRunAt = now + Math.max(0, delayMs);

  const existing = unreadDebounceByClient.get(queryClient);

  if (existing && existing.nextRunAt <= candidateRunAt) {
    return;
  }

  if (existing) {
    clearTimeout(existing.timeoutId);
  }

  const timeoutId = setTimeout(
    () => {
      unreadDebounceByClient.delete(queryClient);

      queryClient.invalidateQueries({ queryKey: ['unreadInquiries'], refetchType: 'active' });
    },
    Math.max(0, candidateRunAt - now)
  );

  unreadDebounceByClient.set(queryClient, { timeoutId, nextRunAt: candidateRunAt });
}

export function cancelUnreadInquiriesInvalidation(queryClient: QueryClient) {
  const existing = unreadDebounceByClient.get(queryClient);

  if (!existing) {
    return;
  }

  clearTimeout(existing.timeoutId);

  unreadDebounceByClient.delete(queryClient);
}
